/**
 * Legend for payroll validate / reconcile metrics shown in Admin Ops health
 * and Payroll Batch Validate panel.
 */
export function PayrollReconcileHelp({ compact = false }: { compact?: boolean }) {
  return (
    <details className={`text-xs text-muted-foreground ${compact ? "mt-1" : "mt-2"}`}>
      <summary className="cursor-pointer select-none font-medium text-foreground/80 hover:text-foreground">
        What do these numbers mean?
      </summary>
      <div className="mt-2 space-y-2 border-l-2 border-border pl-3">
        <p>
          <strong className="text-foreground">Candidates</strong> — punch rows in the date range
          (after co-code / office filters). Not all become ADP lines.
        </p>
        <p>
          <strong className="text-foreground">Included</strong> — punches that count toward payroll
          hours (non-exempt, valid File #, duration &gt; 0, not invalid).
        </p>
        <p>
          <strong className="text-foreground">Excluded</strong> — punches dropped on purpose, e.g.:
        </p>
        <ul className="list-inside list-disc space-y-0.5 pl-1">
          <li>
            <code className="text-[10px]">exempt_payroll_code_E</code> — salaried / exempt (no hours
            file)
          </li>
          <li>
            <code className="text-[10px]">missing_payroll_file_number</code> — user has no ADP File #
            (<code className="text-[10px]">qb_payroll_id</code>)
          </li>
          <li>
            <code className="text-[10px]">open_or_zero_duration</code> — still clocked in (no clock-out),
            so hours cannot be paid
          </li>
          <li>
            <code className="text-[10px]">zero_or_null_duration</code> — clocked out, but duration is
            null or 0 (zero-length or uncomputed shift)
          </li>
          <li>
            <code className="text-[10px]">invalid_punch</code> — bad in/out timing
          </li>
        </ul>
        <p className="text-[11px]">
          <strong className="text-foreground">Excluded (N)</strong> is always the live count of
          punches dropped for this period — it is not a fixed number. Use the exclusion breakdown
          to see why. Exempt and open-punch drops are often expected; many{" "}
          <code className="text-[10px]">zero_or_null_duration</code> closed punches may need a time
          fix; many <code className="text-[10px]">missing_payroll_file_number</code> means assign
          File #s.
        </p>
        <p>
          <strong className="text-foreground">Missing File #</strong> (on the summary line) counts{" "}
          <em>ADP export rows that still have a blank File # cell</em> — different from exclusion
          reason <code className="text-[10px]">missing_payroll_file_number</code> (those punches
          never become rows). Summary should stay{" "}
          <strong className="text-foreground">0</strong>. Fix employee{" "}
          <code className="text-[10px]">qb_payroll_id</code> / identity, then re-run.
        </p>
        <p>
          <strong className="text-foreground">Missing Co Code</strong> — export rows with blank Co
          Code (entry and user both empty). Should be <strong className="text-foreground">0</strong>{" "}
          for clean ADP Batch IDs.
        </p>
        <p>
          <strong className="text-foreground">Δh</strong> — included punch durations minus (Reg + OT)
          on the file. <strong className="text-foreground">0</strong> = hours match. Small values
          (~0.01) can be rounding; &gt; ~0.05 is investigated.
        </p>
        <p>
          <strong className="text-foreground">Payroll daily rows</strong> — number of lines that
          would appear in the ADP CSV (one per employee / co / day with hours).
        </p>
        <p>
          <strong className="text-foreground">Approvals</strong> — whether those employees have
          Approve/Dispute on the pay period (separate from excluded punches).
        </p>
      </div>
    </details>
  );
}
